import { TIME } from 'client/utils/time';
import { cookie } from 'client/utils/cookie/cookie';

const ZIP_REGEX = /^\d{5}$/;
const COORDINATE_REGEX = /^[-+]?\d*\.?\d+$/;
const INTERNATIONAL_TEST_ZIP = '00000';

export function isValidZip(zip) {
  return ZIP_REGEX.test(zip);
}

export function getValidZipStr(zipToTest, oldZip = '') {
  const result = isValidZip(zipToTest) ? zipToTest || '' : oldZip || '';
  return String(result);
}

export function isValidCoordinate(coordinate) {
  return COORDINATE_REGEX.test(coordinate);
}

const DMA_NUMBER_REGEX = /\d+/;

/**
 * Normalizes DMA value, e.g. d803 => 803
 *
 * @param {String} dma Unparsed dma value, e.g. d803
 * @return {String}    Parsed dma value, e.g. 803
 */
export function parseDma(dma) {
  if (!dma) return undefined;
  const matched = dma.match(DMA_NUMBER_REGEX);
  return matched && matched[0];
}

export function parseEdmundsYear(edmundsYear) {
  if (!edmundsYear) return null;

  // parse EdmundsYear string into the object with key/value pairs
  const values = edmundsYear.split('&');
  const parsedEdmundsYear = values.reduce((edmundsYearSet, keyValuePair) => {
    const [key, value] = keyValuePair.split('=');
    if (!key) return edmundsYearSet;
    edmundsYearSet[key] = value; // eslint-disable-line no-param-reassign
    return edmundsYearSet;
  }, {});

  // parse dma value:
  // a) 803:ZIP => { dma: 803, userSet: true }
  // b) 803:IP => { dma: 803, userSet: false }
  if (parsedEdmundsYear.dma) {
    const [dmaValue, dmaSource] = parsedEdmundsYear.dma.split(':');
    parsedEdmundsYear.dma = dmaValue;
    parsedEdmundsYear.userSet = dmaSource === 'ZIP'; // could be either ZIP or IP
  } else {
    parsedEdmundsYear.userSet = false;
  }

  return parsedEdmundsYear;
}

export function parseLocation(locationData, userSet = false, defaultLocation = {}, customLocation = {}) {
  // location data returned by zip api could be either array or a regular object
  let matchedLocation = Array.isArray(locationData) ? locationData[0] : locationData;
  let isUserSet = !!userSet;

  // if location is not defined or zip is invalid use default location and mark userSet as false
  if (!(matchedLocation && isValidZip(matchedLocation.zipCode) && matchedLocation.zipCode !== INTERNATIONAL_TEST_ZIP)) {
    matchedLocation = defaultLocation;
    isUserSet = false;
  }

  const dma = parseDma(matchedLocation.dma);

  const location = {
    ...matchedLocation,
    dma,
    userSet: isUserSet,
    ...customLocation,
  };
  if (!isUserSet) {
    location.ipZipCode = matchedLocation.zipCode;
    location.ipDma = dma;
    location.ipStateCode = matchedLocation.stateCode;
  }
  return location;
}

export function setLocationCookie(location) {
  cookie.set('location', location, {
    domain: '.edmunds.com',
    httpOnly: false,
    secure: true,
    maxAge: TIME.MS.THIRTY_MINUTES,
    path: '/',
  });
}

export function mapEdmundsYearCookie(location, edmundsYear) {
  if (!location) return null;

  const { latitude: lat, longitude: lon, dma, userSet, city, stateCode: state, zipCode: zip } = location;
  const resultObj = {
    ...edmundsYear,
    zip,
    dma: `${dma}:${userSet ? 'ZIP' : 'IP'}`,
    city,
    state,
    lat,
    lon,
  };
  return Object.keys(resultObj).reduce((acc, cur) => `${acc}&${cur}=${resultObj[cur]}`, '');
}

export function setEdmundsYearCookie(edmundsYear) {
  // Need to wrap the cookie value in quotes to adhere to version 1 cookie spec,
  // since it can contain a colon (not allowed in version 0)
  cookie.set('EdmundsYear', `"${edmundsYear}"`, {
    domain: '.edmunds.com',
    httpOnly: false,
    secure: true,
    maxAge: TIME.MS.ONE_YEAR,
    encode: cookieStr => cookieStr,
    path: '/',
  });
}
